import React, { forwardRef, useState, useId, Fragment } from 'react';
import { clsx } from 'clsx';
import { Combobox, Transition } from '@headlessui/react';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const EMPTY_OPTIONS = [];
const EMPTY_VALUES = [];

/**
 * Input Component
 *
 * A polished, accessible input component with floating labels,
 * icons, validation states, and more.
 *
 * @example
 * <Input label="Email" type="email" placeholder="you@example.com" />
 * <Input label="Password" type="password" error="Password is required" />
 * <Input label="Search" leftIcon={<SearchIcon />} />
 */

const inputSizes = {
  sm: 'py-1.5 text-base sm:text-sm min-h-[36px]',
  md: 'py-2.5 text-base sm:text-sm min-h-[42px]',
  lg: 'py-3 text-base min-h-[48px]',
};

const labelSizes = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

const Input = forwardRef(
  (
    {
      label,
      error,
      success,
      hint,
      required = false,
      disabled = false,
      readOnly = false,
      size = 'md',
      leftIcon,
      rightIcon,
      className,
      wrapperClassName,
      type = 'text',
      showPasswordToggle = true,
      characterCount,
      maxLength,
      value,
      onChange,
      onBlur,
      touched = true, // Show validation on blur by default
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [hasBeenTouched, setHasBeenTouched] = useState(false);

    const isPassword = type === 'password';
    const actualType = isPassword && showPassword ? 'text' : type;
    const showError = error && (touched || hasBeenTouched);
    const showSuccess = success && !showError && (touched || hasBeenTouched);

    const hasLeftIcon = !!leftIcon;
    const hasRightElement = !!rightIcon || showError || showSuccess || (isPassword && showPasswordToggle);

    const currentLength = value?.length || 0;
    const isNearLimit = maxLength && currentLength >= maxLength * 0.9;
    const isAtLimit = maxLength && currentLength >= maxLength;

    const handleBlur = (e) => {
      setIsFocused(false);
      setHasBeenTouched(true);
      onBlur?.(e);
    };

    return (
      <div className={clsx('w-full', wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              'block font-medium text-gray-700 mb-1.5',
              labelSizes[size],
              disabled && 'text-gray-400'
            )}
          >
            {label}
            {required && <span className="text-error-500 ml-0.5">*</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          {/* Left icon */}
          {hasLeftIcon && (
            <div
              className={clsx(
                'absolute left-3 top-1/2 -translate-y-1/2',
                'size-5 text-gray-400 pointer-events-none',
                isFocused && 'text-gray-500',
                showError && 'text-error-400',
                showSuccess && 'text-success-400'
              )}
            >
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={id}
            type={actualType}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            required={required}
            aria-invalid={showError ? 'true' : undefined}
            aria-describedby={
              showError
                ? `${id}-error`
                : hint
                ? `${id}-hint`
                : undefined
            }
            className={clsx(
              // Base styles
              'w-full px-3 bg-white rounded-lg',
              'border outline-none',
              'transition-colors duration-200',
              'placeholder:text-gray-400',

              // Size
              inputSizes[size],

              // Padding adjustments for icons
              hasLeftIcon && 'pl-10',
              hasRightElement && 'pr-10',

              // States
              !showError && !showSuccess && [
                'border-gray-200',
                'hover:border-gray-300',
                'focus:border-wrg-blue-500 focus:ring-2 focus:ring-wrg-blue-500/20',
              ],

              // Error state
              showError && [
                'border-error-300 bg-error-50/50',
                'focus:border-error-500 focus:ring-2 focus:ring-error-500/20',
              ],

              // Success state
              showSuccess && [
                'border-success-300 bg-success-50/50',
                'focus:border-success-500 focus:ring-2 focus:ring-success-500/20',
              ],

              // Disabled
              disabled && [
                'bg-gray-50 text-gray-500 cursor-not-allowed',
                'border-gray-200',
              ],

              // Read-only
              readOnly && 'bg-gray-50 cursor-default',

              className
            )}
            {...props}
          />

          {/* Right elements */}
          <div
            className={clsx(
              'absolute right-3 top-1/2 -translate-y-1/2',
              'flex items-center gap-2'
            )}
          >
            {/* Custom right icon */}
            {rightIcon && !isPassword && !showError && !showSuccess && (
              <span className="size-5 text-gray-400">{rightIcon}</span>
            )}

            {/* Error icon */}
            {showError && (
              <ExclamationCircleIcon className="size-5 text-error-500" />
            )}

            {/* Success icon */}
            {showSuccess && (
              <CheckCircleIcon className="size-5 text-success-500" />
            )}

            {/* Password toggle */}
            {isPassword && showPasswordToggle && !disabled && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={clsx(
                  'p-1 -m-1 rounded',
                  'text-gray-400 hover:text-gray-600',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500'
                )}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="size-5" />
                ) : (
                  <EyeIcon className="size-5" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Bottom row: error/hint and character count */}
        <div className="flex items-start justify-between mt-1.5 gap-4">
          {/* Error message */}
          {showError && (
            <p
              id={`${id}-error`}
              className="text-xs text-error-600 flex items-center gap-1"
              role="alert"
            >
              <ExclamationCircleIcon className="size-3.5 flex-shrink-0" />
              <span>{error}</span>
            </p>
          )}

          {/* Hint text (shown when no error) */}
          {hint && !showError && (
            <p id={`${id}-hint`} className="text-xs text-gray-500">
              {hint}
            </p>
          )}

          {/* Spacer */}
          {!showError && !hint && characterCount && <div />}

          {/* Character count */}
          {characterCount && maxLength && (
            <p
              className={clsx(
                'text-xs ml-auto flex-shrink-0',
                isAtLimit
                  ? 'text-error-600 font-medium'
                  : isNearLimit
                  ? 'text-warning-600'
                  : 'text-gray-400'
              )}
            >
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Textarea Component
 */
const Textarea = forwardRef(
  (
    {
      label,
      error,
      success,
      hint,
      required = false,
      disabled = false,
      readOnly = false,
      className,
      wrapperClassName,
      characterCount = false,
      maxLength,
      value,
      onChange,
      onBlur,
      touched = true,
      id: providedId,
      rows = 4,
      resize = 'vertical', // 'none', 'vertical', 'horizontal', 'both'
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const [hasBeenTouched, setHasBeenTouched] = useState(false);

    const showError = error && (touched || hasBeenTouched);
    const showSuccess = success && !showError && (touched || hasBeenTouched);

    const currentLength = value?.length || 0;
    const isNearLimit = maxLength && currentLength >= maxLength * 0.9;
    const isAtLimit = maxLength && currentLength >= maxLength;

    const resizeClasses = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const handleBlur = (e) => {
      setHasBeenTouched(true);
      onBlur?.(e);
    };

    return (
      <div className={clsx('w-full', wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              'block text-sm font-medium text-gray-700 mb-1.5',
              disabled && 'text-gray-400'
            )}
          >
            {label}
            {required && <span className="text-error-500 ml-0.5">*</span>}
          </label>
        )}

        {/* Textarea */}
        <textarea
          ref={ref}
          id={id}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          required={required}
          rows={rows}
          aria-invalid={showError ? 'true' : undefined}
          aria-describedby={
            showError ? `${id}-error` : hint ? `${id}-hint` : undefined
          }
          className={clsx(
            // Base styles
            'w-full px-3 py-2.5 bg-white rounded-lg',
            'border outline-none',
            'transition-colors duration-200',
            'placeholder:text-gray-400',
            resizeClasses[resize],

            // States
            !showError && !showSuccess && [
              'border-gray-200',
              'hover:border-gray-300',
              'focus:border-wrg-blue-500 focus:ring-2 focus:ring-wrg-blue-500/20',
            ],

            // Error state
            showError && [
              'border-error-300 bg-error-50/50',
              'focus:border-error-500 focus:ring-2 focus:ring-error-500/20',
            ],

            // Success state
            showSuccess && [
              'border-success-300 bg-success-50/50',
              'focus:border-success-500 focus:ring-2 focus:ring-success-500/20',
            ],

            // Disabled
            disabled && [
              'bg-gray-50 text-gray-500 cursor-not-allowed',
              'border-gray-200',
            ],

            // Read-only
            readOnly && 'bg-gray-50 cursor-default',

            className
          )}
          {...props}
        />

        {/* Bottom row: error/hint and character count */}
        <div className="flex items-start justify-between mt-1.5 gap-4">
          {/* Error message */}
          {showError && (
            <p
              id={`${id}-error`}
              className="text-xs text-error-600 flex items-center gap-1"
              role="alert"
            >
              <ExclamationCircleIcon className="size-3.5 flex-shrink-0" />
              <span>{error}</span>
            </p>
          )}

          {/* Hint text (shown when no error) */}
          {hint && !showError && (
            <p id={`${id}-hint`} className="text-xs text-gray-500">
              {hint}
            </p>
          )}

          {/* Spacer */}
          {!showError && !hint && characterCount && <div />}

          {/* Character count */}
          {characterCount && maxLength && (
            <p
              className={clsx(
                'text-xs ml-auto flex-shrink-0',
                isAtLimit
                  ? 'text-error-600 font-medium'
                  : isNearLimit
                  ? 'text-warning-600'
                  : 'text-gray-400'
              )}
            >
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

/**
 * Select Component
 */
const Select = forwardRef(
  (
    {
      label,
      error,
      hint,
      required = false,
      disabled = false,
      size = 'md',
      className,
      wrapperClassName,
      children,
      placeholder,
      id: providedId,
      onBlur,
      touched = true,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const [hasBeenTouched, setHasBeenTouched] = useState(false);

    const showError = error && (touched || hasBeenTouched);

    const handleBlur = (e) => {
      setHasBeenTouched(true);
      onBlur?.(e);
    };

    return (
      <div className={clsx('w-full', wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              'block font-medium text-gray-700 mb-1.5',
              labelSizes[size],
              disabled && 'text-gray-400'
            )}
          >
            {label}
            {required && <span className="text-error-500 ml-0.5">*</span>}
          </label>
        )}

        {/* Select wrapper */}
        <div className="relative">
          <select
            ref={ref}
            id={id}
            disabled={disabled}
            required={required}
            onBlur={handleBlur}
            aria-invalid={showError ? 'true' : undefined}
            aria-describedby={
              showError ? `${id}-error` : hint ? `${id}-hint` : undefined
            }
            className={clsx(
              // Base styles
              'w-full px-3 pr-10 bg-white rounded-lg',
              'border outline-none appearance-none',
              'transition-colors duration-200',

              // Size
              inputSizes[size],

              // States
              !showError && [
                'border-gray-200',
                'hover:border-gray-300',
                'focus:border-wrg-blue-500 focus:ring-2 focus:ring-wrg-blue-500/20',
              ],

              // Error state
              showError && [
                'border-error-300 bg-error-50/50',
                'focus:border-error-500 focus:ring-2 focus:ring-error-500/20',
              ],

              // Disabled
              disabled && [
                'bg-gray-50 text-gray-500 cursor-not-allowed',
                'border-gray-200',
              ],

              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {children}
          </select>

          {/* Dropdown arrow */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className={clsx(
                'size-5',
                disabled ? 'text-gray-300' : 'text-gray-400'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Error message */}
        {showError && (
          <p
            id={`${id}-error`}
            className="mt-1.5 text-xs text-error-600 flex items-center gap-1"
            role="alert"
          >
            <ExclamationCircleIcon className="size-3.5 flex-shrink-0" />
            <span>{error}</span>
          </p>
        )}

        {/* Hint text (shown when no error) */}
        {hint && !showError && (
          <p id={`${id}-hint`} className="mt-1.5 text-xs text-gray-500">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

/**
 * SearchableSelect Component
 *
 * A searchable dropdown using Headless UI Combobox.
 * Allows users to type to filter options.
 *
 * @example
 * <SearchableSelect
 *   label="Department"
 *   options={departments}
 *   value={selectedDept}
 *   onChange={setSelectedDept}
 *   valueKey="department_ID"
 *   labelKey="name"
 *   placeholder="All Departments"
 * />
 */
const SearchableSelect = ({
  label,
  options = EMPTY_OPTIONS,
  value,
  onChange,
  valueKey = 'value',
  labelKey = 'label',
  placeholder = 'Select…',
  disabled = false,
  size = 'md',
  className,
  wrapperClassName,
  allowClear = true,
  emptyMessage = 'No options found',
}) => {
  const [query, setQuery] = useState('');

  // Find the selected option
  const selectedOption = options.find(
    (opt) => String(opt[valueKey]) === String(value)
  );

  // Filter options based on search query
  const filteredOptions =
    query === ''
      ? options
      : options.filter((opt) => {
          const label = opt[labelKey] || '';
          const searchableText = typeof opt.searchText === 'string'
            ? opt.searchText
            : label;
          return searchableText.toLowerCase().includes(query.toLowerCase());
        });

  const handleChange = (option) => {
    onChange(option ? option[valueKey] : '');
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  return (
    <div className={clsx('w-full', wrapperClassName)}>
      <Combobox
        value={selectedOption || null}
        onChange={handleChange}
        disabled={disabled}
        nullable
      >
        {label && (
          <Combobox.Label
            className={clsx(
              'block font-medium text-gray-700 mb-1.5',
              labelSizes[size],
              disabled && 'text-gray-400'
            )}
          >
            {label}
          </Combobox.Label>
        )}
        <div className="relative">
          <div className="relative">
            <Combobox.Input
              className={clsx(
                'w-full pl-3 pr-10 bg-white rounded-lg',
                'border outline-none',
                'transition-colors duration-200',
                'placeholder:text-gray-400',
                inputSizes[size],
                'border-gray-200',
                'hover:border-gray-300',
                'focus:border-wrg-blue-500 focus:ring-2 focus:ring-wrg-blue-500/20',
                disabled && [
                  'bg-gray-50 text-gray-500 cursor-not-allowed',
                  'border-gray-200',
                ],
                className
              )}
              displayValue={(option) =>
                option ? option[labelKey] : ''
              }
              onChange={(e) => setQuery(e.target.value)}
              placeholder={selectedOption ? selectedOption[labelKey] : placeholder}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
              {allowClear && value && !disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  aria-label="Clear selection"
                >
                  <XMarkIcon className="size-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
              <Combobox.Button className="p-1" aria-label="Show options">
                <ChevronUpDownIcon
                  className="size-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options
              className={clsx(
                'absolute z-50 mt-1 w-full',
                'max-h-60 overflow-auto',
                'rounded-lg bg-white',
                'border border-gray-200 shadow-lg',
                'focus:outline-none'
              )}
            >
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="py-3 px-4 text-sm text-gray-500 text-center">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  {/* Clear/All option */}
                  {allowClear && (
                    <Combobox.Option
                      value={null}
                      className={({ active }) =>
                        clsx(
                          'relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-sm',
                          active ? 'bg-wrg-blue-50 text-wrg-blue-900' : 'text-gray-700'
                        )
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={clsx(
                              'block truncate',
                              selected ? 'font-medium' : 'font-normal'
                            )}
                          >
                            {placeholder}
                          </span>
                          {!value && (
                            <span
                              className={clsx(
                                'absolute inset-y-0 left-0 flex items-center pl-3',
                                active ? 'text-wrg-blue-600' : 'text-wrg-blue-500'
                              )}
                            >
                              <CheckIcon className="size-4" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  )}

                  {filteredOptions.map((option) => (
                    <Combobox.Option
                      key={option[valueKey]}
                      value={option}
                      className={({ active }) =>
                        clsx(
                          'relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-sm',
                          active ? 'bg-wrg-blue-50 text-wrg-blue-900' : 'text-gray-700'
                        )
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={clsx(
                              'block truncate',
                              selected ? 'font-medium' : 'font-normal'
                            )}
                          >
                            {option[labelKey]}
                            {option.subtitle && (
                              <span className="text-gray-400 ml-1">
                                ({option.subtitle})
                              </span>
                            )}
                          </span>
                          {selected && (
                            <span
                              className={clsx(
                                'absolute inset-y-0 left-0 flex items-center pl-3',
                                active ? 'text-wrg-blue-600' : 'text-wrg-blue-500'
                              )}
                            >
                              <CheckIcon className="size-4" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

SearchableSelect.displayName = 'SearchableSelect';

/**
 * MultiSearchableSelect Component
 *
 * A multi-select searchable dropdown using Headless UI Combobox.
 * Allows users to select multiple options with type-to-filter.
 *
 * @example
 * <MultiSearchableSelect
 *   label="Departments"
 *   options={departments}
 *   value={selectedDepts}
 *   onChange={setSelectedDepts}
 *   valueKey="department_ID"
 *   labelKey="name"
 *   placeholder="All Departments"
 * />
 */
const MultiSearchableSelect = ({
  label,
  options = EMPTY_OPTIONS,
  value = EMPTY_VALUES,
  onChange,
  valueKey = 'value',
  labelKey = 'label',
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled = false,
  size = 'md',
  className,
  wrapperClassName,
  emptyMessage = 'No options found',
  maxDisplayedChips = 3,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  // Ensure value is always an array
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  // Find selected options
  const selectedOptions = options.filter(
    (opt) => selectedValues.includes(String(opt[valueKey])) || selectedValues.includes(opt[valueKey])
  );

  // Filter options based on search query
  const filteredOptions =
    query === ''
      ? options
      : options.filter((opt) => {
          const labelText = opt[labelKey] || '';
          const searchableText = typeof opt.searchText === 'string'
            ? opt.searchText
            : labelText;
          return searchableText.toLowerCase().includes(query.toLowerCase());
        });

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (optionValue) => {
    const strValue = String(optionValue);
    const newValues = selectedValues.includes(strValue) || selectedValues.includes(optionValue)
      ? selectedValues.filter(v => String(v) !== strValue)
      : [...selectedValues, strValue];
    onChange(newValues);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setQuery('');
  };

  const handleRemoveChip = (e, optionValue) => {
    e.stopPropagation();
    const strValue = String(optionValue);
    onChange(selectedValues.filter(v => String(v) !== strValue));
  };

  const isSelected = (optionValue) => {
    const strValue = String(optionValue);
    return selectedValues.includes(strValue) || selectedValues.includes(optionValue);
  };

  return (
    <div ref={wrapperRef} className={clsx('w-full', wrapperClassName)}>
      {label && (
        <label
          className={clsx(
            'block font-medium text-gray-700 mb-1.5',
            labelSizes[size],
            disabled && 'text-gray-400'
          )}
        >
          {label}
        </label>
      )}

      <div className="relative">
        {/* Trigger */}
        <div
          className={clsx(
            'w-full text-left bg-white rounded-lg',
            'border outline-none',
            'transition-colors duration-200',
            inputSizes[size],
            'border-gray-200',
            'hover:border-gray-300',
            isOpen && 'border-wrg-blue-500 ring-2 ring-wrg-blue-500/20',
            disabled && [
              'bg-gray-50 text-gray-500 cursor-not-allowed',
              'border-gray-200',
            ],
            className
          )}
        >
          <div className="flex items-center justify-between pr-2">
            <div className="flex-1 flex flex-wrap items-center gap-1 min-h-[24px] pl-3 pr-2 py-1 min-w-0">
              {selectedOptions.length > 0 &&
                selectedOptions.length <= maxDisplayedChips &&
                selectedOptions.map((opt) => (
                  <span
                    key={opt[valueKey]}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-wrg-blue-100 text-wrg-blue-700 rounded text-xs font-medium"
                  >
                    {opt[labelKey]}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveChip(e, opt[valueKey])}
                      disabled={disabled}
                      aria-label={`Remove ${opt[labelKey]}`}
                      className="hover:text-wrg-blue-900 disabled:cursor-not-allowed"
                    >
                      <XMarkIcon className="size-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={label || placeholder}
                className={clsx(
                  'flex-1 min-w-0 self-stretch flex items-center justify-between gap-1 text-left bg-transparent outline-none',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
              >
                <span className="min-w-0 truncate">
                  {selectedOptions.length === 0 && (
                    <span className="text-gray-400">{placeholder}</span>
                  )}
                  {selectedOptions.length > maxDisplayedChips && (
                    <span className="text-gray-700">
                      {selectedOptions.length} selected
                    </span>
                  )}
                </span>
                <ChevronUpDownIcon
                  className="size-5 text-gray-400 flex-shrink-0"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {selectedValues.length > 0 && !disabled && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  aria-label="Clear selections"
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="size-4 text-gray-400 hover:text-gray-600" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            className={clsx(
              'absolute z-50 mt-1 w-full',
              'max-h-60 overflow-auto',
              'rounded-lg bg-white',
              'border border-gray-200 shadow-lg'
            )}
          >
            {/* Search input */}
            <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-9 pr-3 py-2 text-base sm:text-sm border border-gray-200 rounded-md focus:border-wrg-blue-500 focus:ring-1 focus:ring-wrg-blue-500/20 focus:outline-none"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
              </div>
            </div>

            {filteredOptions.length === 0 && query !== '' ? (
              <div className="py-3 px-4 text-sm text-gray-500 text-center">
                {emptyMessage}
              </div>
            ) : (
              <div className="py-1">
                {/* Select All / Clear All */}
                {filteredOptions.length > 0 && (
                  <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => {
                        const allValues = filteredOptions.map(opt => String(opt[valueKey]));
                        const allSelected = allValues.every(v => selectedValues.includes(v));
                        if (allSelected) {
                          // Deselect all filtered
                          onChange(selectedValues.filter(v => !allValues.includes(String(v))));
                        } else {
                          // Select all filtered (merge with existing)
                          const newValues = [...new Set([...selectedValues, ...allValues])];
                          onChange(newValues);
                        }
                      }}
                      className="text-xs text-wrg-blue-600 hover:text-wrg-blue-700 font-medium"
                    >
                      {filteredOptions.every(opt => isSelected(opt[valueKey])) ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedValues.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {selectedValues.length} selected
                      </span>
                    )}
                  </div>
                )}

                {filteredOptions.map((option) => (
                  <button
                    key={option[valueKey]}
                    type="button"
                    onClick={() => handleToggleOption(option[valueKey])}
                    className={clsx(
                      'w-full text-left px-3 py-2.5 text-sm flex items-center gap-3',
                      'hover:bg-wrg-blue-50 transition-colors',
                      isSelected(option[valueKey]) && 'bg-wrg-blue-50/50'
                    )}
                  >
                    <div
                      className={clsx(
                        'size-4 rounded border flex items-center justify-center flex-shrink-0',
                        isSelected(option[valueKey])
                          ? 'bg-wrg-blue-500 border-wrg-blue-500'
                          : 'border-gray-300'
                      )}
                    >
                      {isSelected(option[valueKey]) && (
                        <CheckIcon className="size-3 text-white" />
                      )}
                    </div>
                    <span className={clsx(
                      'flex-1 truncate',
                      isSelected(option[valueKey]) ? 'font-medium text-wrg-blue-900' : 'text-gray-700'
                    )}>
                      {option[labelKey]}
                      {option.subtitle && (
                        <span className="text-gray-400 ml-1">
                          ({option.subtitle})
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

MultiSearchableSelect.displayName = 'MultiSearchableSelect';

/**
 * Checkbox Component
 */
const Checkbox = forwardRef(
  (
    {
      label,
      description,
      error,
      disabled = false,
      className,
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;

    return (
      <div className={clsx('relative flex items-start', className)}>
        <div className="flex items-center h-5">
          <input
            ref={ref}
            type="checkbox"
            id={id}
            disabled={disabled}
            className={clsx(
              'size-4 rounded',
              'border-gray-300',
              'text-wrg-blue-500',
              'focus:ring-2 focus:ring-wrg-blue-500/20 focus:ring-offset-0',
              'transition-colors duration-150',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            {...props}
          />
        </div>
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={id}
                className={clsx(
                  'text-sm font-medium text-gray-700',
                  disabled && 'text-gray-400'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

/**
 * Form Group - for grouping related form fields
 */
const FormGroup = forwardRef(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('space-y-4', className)} {...props}>
        {children}
      </div>
    );
  }
);

FormGroup.displayName = 'FormGroup';

/**
 * Form Row - for horizontal layout of form fields
 */
const FormRow = forwardRef(
  ({ children, className, cols = 2, ...props }, ref) => {
    const colClasses = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    };

    return (
      <div
        ref={ref}
        className={clsx('grid gap-4', colClasses[cols], className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

FormRow.displayName = 'FormRow';

export { Input, Textarea, Select, SearchableSelect, MultiSearchableSelect, Checkbox, FormGroup, FormRow };
export default Input;
